import { Component, WritableSignal, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

interface Breadcrumb {
  label: string;
  url: UrlSegment[];
  /** La radice serve a costruire gli indirizzi, ma in pagina la disegna il template. */
  eHome: boolean;
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [MatIconModule, TranslateModule],
  templateUrl: './breadcrumb.component.html',
})
export class BreadcrumbComponent {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  breadcrumbs$: WritableSignal<Breadcrumb[]> = signal<Breadcrumb[]>([]);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.buildBreadCrumb(this.activatedRoute.root))
      )
      .subscribe((breadcrumbs) => {
        this.breadcrumbs$.set(breadcrumbs);
      });
  }

  private buildBreadCrumb(route: ActivatedRoute, breadcrumb: Breadcrumb[] = []): Breadcrumb[] {
    const data: Partial<{ breadcrumb: { label: string } }> | undefined = route.routeConfig?.data;
    if (breadcrumb.length === 0) {
      breadcrumb.push({
        label: 'breadcrumb.home',
        url: [new UrlSegment('/', {})],
        eHome: true,
      });
    }

    const label = data?.breadcrumb?.label ?? '';
    breadcrumb.push({
      label,
      url: [...breadcrumb[breadcrumb.length - 1].url, ...route.snapshot.url],
      eHome: false,
    });

    if (route.firstChild) {
      this.buildBreadCrumb(route.firstChild, breadcrumb);
    }
    return this.removeEmptyBreadcrumbs(breadcrumb);
  }

  private removeEmptyBreadcrumbs(breadcrumbs: Breadcrumb[]): Breadcrumb[] {
    return breadcrumbs.filter(({ label, eHome }) => !eHome && label);
  }

  redirectTo(url?: UrlSegment[]): void {
    if (url) {
      this.router.navigate(
        url.map(({ path }) => path),
        { queryParamsHandling: 'preserve' }
      );
    }
  }
}
